import type { PatientListItem, PatientListResponse } from "../../../shared/api-types";

// The RD's Patients list (NO-835): which page state to render, what the list
// holds, and how a visit date reads. Pure and relative-import only, so
// `tsx --test` runs it.

export const PATIENTS_QUERY_KEY = ["patients"] as const;

export type PatientsPageState = "blank" | "not_found" | "page";

/**
 * The single-rail frame only, and only with `patient-detail` on for a viewer
 * who has their own Appointments: everyone else gets the router's 404, like
 * any other flagged page. Blank while the flags and the viewer load.
 */
export function patientsPageState(input: {
  frame: "rail" | "classic" | null;
  featuresLoading: boolean;
  patientDetailEnabled: boolean;
  canReadOwnAppointments: boolean;
}): PatientsPageState {
  if (input.frame !== "rail") return "not_found";

  if (input.featuresLoading) return "blank";

  return input.patientDetailEnabled && input.canReadOwnAppointments
    ? "page"
    : "not_found";
}

/**
 * What the list shows. `no_selection` only answers an admin with no RD chosen,
 * which this page never asks for, so it reads as an empty list.
 */
export type PatientListView =
  | { kind: "list"; patients: PatientListItem[] }
  | { kind: "empty" }
  | { kind: "unavailable" };

export function patientListView(response: PatientListResponse): PatientListView {
  if (response.status === "unavailable") return { kind: "unavailable" };

  if (response.status === "no_selection" || response.patients.length === 0) {
    return { kind: "empty" };
  }

  return { kind: "list", patients: response.patients };
}

/** "Sep 30", with the year added when it is not the current one. */
export function visitDateLabel(iso: string, timezone: string, now: Date): string {
  const yearOf = (date: Date) =>
    new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric" }).format(
      date
    );
  const date = new Date(iso);
  const sameYear = yearOf(date) === yearOf(now);

  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(date);
}

export function lastVisitLabel(
  patient: Pick<PatientListItem, "lastVisitAt">,
  timezone: string,
  now: Date
): string {
  return patient.lastVisitAt === null
    ? "None yet"
    : visitDateLabel(patient.lastVisitAt, timezone, now);
}

export function nextVisitLabel(
  patient: Pick<PatientListItem, "nextVisitAt">,
  timezone: string,
  now: Date
): string {
  return patient.nextVisitAt === null
    ? "Not scheduled"
    : visitDateLabel(patient.nextVisitAt, timezone, now);
}

/** The Client's name as the list shows it; a nameless Client still gets a row. */
export function patientName(patient: Pick<PatientListItem, "displayName">): string {
  return patient.displayName?.trim() || "Unnamed Client";
}
